import React from 'react';
import {useRef, useMemo} from 'react';
import {VisualizationSpec} from 'react-vega';
import CustomPanelRenderer from '../Vega3/CustomPanelRenderer';
import {useGatedValue, useOnScreen} from '../../state/hooks';
import * as Panel2 from './panel';
import * as CGReact from './cg/cgreact';

const inputType = {
  type: 'array' as const,
  objectType: {
    type: 'union' as const,
    members: [null, 'string' as const],
  },
};

type PanelStringHistogramProps = Panel2.PanelProps<typeof inputType>;

/* eslint-disable no-template-curly-in-string */

const HISTO_SPEC: VisualizationSpec = {
  $schema: 'https://vega.github.io/schema/vega-lite/v4.json',
  description: 'A simple histogram',
  data: {
    name: 'wandb',
  },
  transform: [
    {
      aggregate: [
        {
          op: 'count',
          as: 'value_count',
        },
      ],
      groupby: ['value'],
    },
    {
      window: [{op: 'row_number', as: 'value_rank'}],
      sort: [
        {
          field: 'value_count',
          order: 'descending',
        },
      ],
    },
    {filter: `datum.value_rank < 100`},
  ],
  title: '${string:title}',
  mark: {type: 'bar', tooltip: {content: 'data'}},
  encoding: {
    y: {
      type: 'nominal',
      field: 'value',
      axis: {
        title: null,
      },
    },
    x: {
      field: 'value_count',
      type: 'quantitative',
    },
    opacity: {value: 0.7},
  },
};

const PanelStringHistogram: React.FC<PanelStringHistogramProps> = props => {
  const domRef = useRef<HTMLDivElement>(null);
  const onScreen = useOnScreen(domRef);
  const hasBeenOnScreen = useGatedValue(onScreen, o => o);
  const nodeValueQuery = CGReact.useNodeValue(props.input.path);
  const data = useMemo(() => {
    if (nodeValueQuery.loading) {
      return [];
    }
    return nodeValueQuery.result.map(v => ({value: v}));
  }, [nodeValueQuery]);
  return (
    <div ref={domRef} style={{width: 400, maxHeight: 200, overflowY: 'auto'}}>
      {hasBeenOnScreen ? (
        <>
          <CustomPanelRenderer
            spec={HISTO_SPEC}
            loading={props.loading}
            slow={false}
            data={data}
            userSettings={{fieldSettings: {}, stringSettings: {title: ''}}}
          />
        </>
      ) : (
        <div
          style={{
            backgroundColor: '#eee',
            margin: 16,
            width: '100%',
            height: '100%',
          }}
        />
      )}
    </div>
  );
};

export const Spec: Panel2.PanelSpec = {
  id: 'string-histogram',
  Component: PanelStringHistogram,
  inputType,
};
